import { Editor, Transforms, type NodeEntry, type Path, type ReactEditor } from '@lumapps/wrex/slate';
import { normalizeLastBlock } from '@lumapps/wrex/slate/utils/normalizeLastBlock';
import { normalizeLiftToRootNode } from '@lumapps/wrex/slate/utils/normalizeLiftToRootNode';
import type { WrexEditor } from '@lumapps/wrex/types';

import type { ColumnsEditor, ColumnsElement } from '../../types';
import { createColumn } from '../../utils/createColumn';
import { isColumn } from '../../utils/isColumn';
import { isColumns } from '../../utils/isColumns';

export const normalizeColumns = ([node, path]: NodeEntry, editor: WrexEditor<ColumnsEditor>) => {
    if (!isColumns(node)) {
        return false;
    }

    const columnsNode = node as ColumnsElement;
    const columnsPathRef = Editor.pathRef(editor, path);

    // Columns must have 2 column as min children
    // if less than 2 children we unwrap
    if (columnsPathRef.current && columnsNode.children.length < 2) {
        Transforms.unwrapNodes(editor, { at: columnsPathRef.current });
        return true;
    }
    // If columns is not at first level, unwrap it
    if (columnsPathRef.current) {
        normalizeLiftToRootNode(editor, [node, columnsPathRef.current], isColumns, { mode: 'after' });
    }
    // Insert a paragraph element after columns if there is not already one.
    if (columnsPathRef.current) {
        normalizeLastBlock(editor as ReactEditor, [columnsNode, path], isColumns);
    }

    if (columnsPathRef.current) {
        editor.withoutNormalizing(() => {
            columnsNode.children
                .map((child, i) => [child, Editor.pathRef(editor, [...(columnsPathRef.current as Path), i])] as const)
                .forEach(([child, pathRef]) => {
                    // Columns must contain only column block type as children
                    if (pathRef.current && !isColumn(child)) {
                        // if child is columns we lift to root
                        if (isColumns(child)) {
                            normalizeLiftToRootNode(editor, [child, pathRef.current], isColumns, { mode: 'after' });
                        } else {
                            // if other block we wrap the current block within a column
                            Transforms.wrapNodes(editor, createColumn(), { at: pathRef.current as Path });
                        }
                        pathRef.unref();
                    }
                });
        });
    }

    return false;
};
